import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiDataResponse, ApiResponse } from '@shared/model/api-response.model';
import { HRGetPRCo_Result } from '@typeDefs/admin/responses/admin';
import { LinkUserParams, VerifyUserParams } from '@typeDefs/account/requests/account';
import { CreateAccountData_Result } from '@typeDefs/account/responses/account';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  http: HttpClient;
  constructor(http: HttpClient) {
    this.http = http;
  }

  // Already moved to api service already
  getCompanies(): Promise<HRGetPRCo_Result> {
    return this.http.get<HRGetPRCo_Result>('/Account/GetCompanies').toPromise();
  }

  login(loginData: any) {
    return this.http
      .post('/Account/Login', {
        actionModel: loginData,
      })
      .toPromise();
  }

  windowsLogin(loginData: any) {
    return this.http.post('/Account/WindowsLogin', loginData).toPromise();
  }

  requestPasswordReset(employeeNum: number, prco: number, inputPin: string) {
    return this.http
      .post('/Account/RequestPasswordReset', {
        employee: employeeNum,
        pRCo: prco,
        inputPin: inputPin,
      })
      .toPromise();
  }

  createAccount(accountData: any) {
    return this.http
      .post('/Account/Create', {
        actionModel: accountData,
      })
      .toPromise();
  }

  checkReturnUrl(returnUrl: string) {
    return this.http.get('/Account/CheckReturnUrl?returnUrl=' + returnUrl).toPromise();
  }

  getCompanyNumFromUrl(coString: string) {
    return this.http.get('/Account/GetCompanyNumFromUrl?coString=' + coString).toPromise();
  }

  namedLogin(username: string, password: string) {
    return this.http.post('/Account/NamedLogin', { username: username, password: password }).toPromise();
  }

  GetIsLoggedIn() {
    return this.http.get<boolean>('/Account/IsLoggedIn').toPromise();
  }

  changePassword(requestObject: any) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    const options = { headers: headers };
    const body = new URLSearchParams();
    body.set('employee', requestObject.employee);
    body.set('company', requestObject.company);
    body.set('oldPassword', requestObject.oldPassword);
    body.set('newPassword', requestObject.newPassword);
    return this.http.post('/Account/ChangePassword', body.toString(), options).toPromise();
  }

  getEmployeeDetails(userKeyID?: number, employee?: number, company?: number) {
    return this.http
      .get('/Account/GetEmployeeDetails?userKeyID=' + userKeyID + '&employee=' + employee + '&company=' + company)
      .toPromise();
  }

  resetPassword(userKeyId: number, inputPin: string, token: string, newPassword: string, confirmPassword: string) {
    return this.http
      .post('/Account/ResetPassword', {
        userKeyId: userKeyId,
        inputPin: inputPin,
        token: token,
        newPassword: newPassword,
        confirmPassword: confirmPassword,
      })
      .toPromise();
  }

  manualResetPassword(newPassword: string, confirmPassword: string, verificationPIN: number) {
    return this.http
      .post('/Account/ManualResetPassword', {
        newPassword: newPassword,
        confirmPassword: confirmPassword,
        verificationPIN: verificationPIN,
      })
      .toPromise();
  }

  async getPortalSettings(): Promise<PortalSettings> {
    return this.http.get<PortalSettings>('/Account/GetPortalSettings').toPromise();
  }

  async getAnonPortalSettings(): Promise<PortalSettings> {
    return this.http.get<PortalSettings>('/Account/GetPortalSettingsAnon').toPromise();
  }

  getJobsAccessToken() {
    return this.http.get('/Account/GetJobsAccessToken').toPromise();
  }

  getAccountLogoutRedirect() {
    const url = '/Account/Logout?noRedirect=true';
    return this.http.get(url, { responseType: 'text' }).toPromise();
  }

  verifyUser(params: VerifyUserParams): Observable<ApiResponse> {
    return this.http.post<ApiResponse>('/Account/VerifyUser', params);
  }

  getOneTimeGuid() {
    return this.http.get('/Account/GetOneTimeGuid').toPromise();
  }

  linkUser(params: LinkUserParams): Observable<ApiResponse> {
    return this.http.post<ApiResponse>('/Account/LinkUser', params);
  }

  createAccountFromToken(userKeyID: number | null, password: string, token: string | null): Observable<ApiResponse> {
    return this.http.post<ApiResponse>('/Account/CreateAccountFromToken', {
      userKeyID: userKeyID,
      password: password,
      token: token,
    });
  }

  getCreateAccountData(token: string): Observable<ApiDataResponse<CreateAccountData_Result>> {
    const params = new HttpParams().set('token', token);
    return this.http.get<ApiDataResponse<CreateAccountData_Result>>('/Account/GetCreateAccountData', { params });
  }
}

export interface PortalSettings {
  [key: string]: { ConfigValue: string | null };
}

